(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const visibleFocusable = (container) =>
    Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      if (element.closest('[aria-hidden="true"]') || element.closest('[inert]')) return false;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;

      const bounds = element.getBoundingClientRect();
      return bounds.width > 0 && bounds.height > 0;
    });

  const overlaySelector = '[role="dialog"],[aria-modal="true"],dialog[open],.fancybox__container';

  const focusIsInForeignOverlay = (container) => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return false;
    const overlay = active.closest(overlaySelector);
    if (!overlay || container.contains(overlay) || overlay.contains(container)) return false;
    return !overlay.hasAttribute('inert') && overlay.getAttribute('aria-hidden') !== 'true';
  };

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const focusable = visibleFocusable(container);
    if (!focusable.length) {
      event.preventDefault();
      container.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const index = focusable.indexOf(document.activeElement);
    if (index === -1) {
      // Focus sits on something we do not recognise as focusable (a wallet custom element,
      // for example). Inside the container that is still contained, so let the browser move
      // natively; snapping would bounce between the unrecognised element and an edge.
      if (container.contains(document.activeElement)) return;
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const initializeModal = (modal) => {
    if (modal.dataset.wcAccessibilityInitialized === 'true') return;
    modal.dataset.wcAccessibilityInitialized = 'true';

    let returnFocus = null;
    let wasOpen = false;
    modal.setAttribute('tabindex', '-1');

    const sync = () => {
      const isOpen = modal.classList.contains('open');
      modal.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      modal.toggleAttribute('inert', !isOpen);

      if (isOpen === wasOpen) return;
      wasOpen = isOpen;

      if (isOpen) {
        returnFocus = document.activeElement;
        window.setTimeout(() => {
          if (!modal.classList.contains('open')) return;
          if (modal.contains(document.activeElement)) return;
          if (focusIsInForeignOverlay(modal)) return;

          const preferred = modal.querySelector('[data-modal-close]');
          (preferred || visibleFocusable(modal)[0] || modal).focus();
        }, 50);
      } else if (returnFocus instanceof HTMLElement && document.contains(returnFocus)) {
        returnFocus.focus();
        returnFocus = null;
      }
    };

    new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ['class'] });
    modal.addEventListener('keydown', (event) => {
      if (!modal.classList.contains('open')) return;
      if (event.key === 'Escape' && typeof modal.handleCloseModal === 'function') {
        event.preventDefault();
        modal.handleCloseModal();
        return;
      }
      trapFocus(event, modal);
    });
    sync();
  };

  const initializeMegaMenu = (component) => {
    const panel = component.querySelector('.mega-menu');
    if (!panel) return;
    const nestedLevels = Array.from(panel.querySelectorAll('.menu-section--second, .menu-section--third'));
    let returnFocus = null;
    let wasOpen = false;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', component.dataset.label || 'Menu');
    panel.setAttribute('tabindex', '-1');

    const trigger = document.querySelector(`[aria-controls="${component.id}"]`);
    const nestedReturnTarget = (level) => {
      let parentLevel = level.previousElementSibling;
      while (parentLevel) {
        const opener = visibleFocusable(parentLevel).find((candidate) =>
          candidate.matches('.menu-item--dropdown.active .menu-arrow'),
        );
        if (opener) return opener;
        parentLevel = parentLevel.previousElementSibling;
      }
      return panel.querySelector('.close-menu') || panel;
    };
    const restoreNestedFocus = (level, attempt = 0) => {
      const focusTarget = nestedReturnTarget(level);
      focusTarget.focus();
      if (attempt >= 2) return;
      window.requestAnimationFrame(() => {
        const targetStillUsable =
          document.activeElement === focusTarget &&
          (focusTarget === panel || visibleFocusable(panel).includes(focusTarget));
        if (!targetStillUsable || attempt === 0) restoreNestedFocus(level, attempt + 1);
      });
    };
    const syncNestedLevel = (level) => {
      const isActive = level.classList.contains('active');
      const hadFocus = level.contains(document.activeElement);
      level.setAttribute('aria-hidden', String(!isActive));
      level.toggleAttribute('inert', !isActive);
      if (!isActive && hadFocus) restoreNestedFocus(level);
    };

    nestedLevels.forEach((level) => {
      new MutationObserver(() => syncNestedLevel(level)).observe(level, {
        attributes: true,
        attributeFilter: ['class'],
      });
      syncNestedLevel(level);
    });

    const sync = () => {
      const isOpen = panel.classList.contains('active');
      panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      panel.toggleAttribute('inert', !isOpen);
      if (trigger) trigger.setAttribute('aria-expanded', String(isOpen));

      if (isOpen === wasOpen) return;
      wasOpen = isOpen;

      if (isOpen) {
        returnFocus = trigger || document.activeElement;
        window.setTimeout(() => {
          (panel.querySelector('.close-menu') || visibleFocusable(panel)[0] || panel).focus();
        }, 50);
      } else if (returnFocus instanceof HTMLElement && document.contains(returnFocus)) {
        returnFocus.focus();
        returnFocus = null;
      }
    };

    new MutationObserver(sync).observe(panel, { attributes: true, attributeFilter: ['class'] });
    panel.addEventListener('keydown', (event) => {
      if (panel.classList.contains('active')) trapFocus(event, panel);
    });
    sync();
  };

  const initializeCartAnnouncements = () => {
    const status = document.getElementById('WcAccessibilityStatus');
    if (!status) return;
    let previousCount = document.querySelector('.header__cart-quantity')?.textContent?.trim();

    document.addEventListener('liquid-ajax-cart:request-end', () => {
      window.setTimeout(() => {
        const count = document.querySelector('.header__cart-quantity')?.textContent?.trim();
        if (count == null) return;

        const message = `${status.dataset.cartLabel}: ${count}`;
        document.querySelectorAll('.js-header-cart').forEach((cartLink) => {
          cartLink.setAttribute('aria-label', message);
        });

        if (count !== previousCount) {
          status.textContent = message;
          previousCount = count;
        }
      }, 0);
    });
  };

  const initializeCartFocusRestoration = () => {
    let lastFocusedControl = null;
    let lastFocusedSelector = null;

    const equivalentSelector = (control) => {
      if (!(control instanceof HTMLElement)) return null;
      if (control.id) return `#${CSS.escape(control.id)}`;

      const label = control.getAttribute('aria-label');
      const labelSelector = label ? `[aria-label=${CSS.escape(label)}]` : '';
      const identifyingAttributes = [
        'data-ajax-cart-quantity-input',
        'data-ajax-cart-quantity-minus',
        'data-ajax-cart-quantity-plus',
        'data-ajax-cart-request-button',
        'data-discount-action-button',
        'name',
      ];

      for (const attribute of identifyingAttributes) {
        if (!control.hasAttribute(attribute)) continue;
        const value = control.getAttribute(attribute);
        const attributeSelector = value ? `[${attribute}=${CSS.escape(value)}]` : `[${attribute}]`;
        return `${attributeSelector}${labelSelector}`;
      }

      return labelSelector || null;
    };

    document.addEventListener('focusin', (event) => {
      const drawer = document.querySelector('custom-modal[data-id="side-cart"]');
      if (!drawer?.classList.contains('open') || !drawer.contains(event.target)) return;
      lastFocusedControl = event.target;
      lastFocusedSelector = equivalentSelector(event.target);
    });

    document.addEventListener('liquid-ajax-cart:request-end', () => {
      const drawer = document.querySelector('custom-modal[data-id="side-cart"]');
      if (!drawer?.classList.contains('open') || drawer.contains(document.activeElement)) return;

      const focusedControl = lastFocusedControl;
      const focusedSelector = lastFocusedSelector;
      const maxAttempts = 10;

      const restoreFrom = (targetFactories, targetIndex = 0, attempt = 0) => {
        const currentDrawer = document.querySelector('custom-modal[data-id="side-cart"]');
        if (!currentDrawer?.classList.contains('open') || currentDrawer.contains(document.activeElement)) return;

        const targetFactory = targetFactories[targetIndex];
        if (!targetFactory) return;
        const target = targetFactory(currentDrawer);
        if (!target) {
          restoreFrom(targetFactories, targetIndex + 1, 0);
          return;
        }

        target.focus();
        if (document.activeElement === target) return;

        if (attempt + 1 < maxAttempts) {
          window.requestAnimationFrame(() => restoreFrom(targetFactories, targetIndex, attempt + 1));
        } else {
          restoreFrom(targetFactories, targetIndex + 1, 0);
        }
      };

      window.requestAnimationFrame(() => {
        restoreFrom([
          (currentDrawer) =>
            focusedControl instanceof HTMLElement && currentDrawer.contains(focusedControl) ? focusedControl : null,
          (currentDrawer) => (focusedSelector ? currentDrawer.querySelector(focusedSelector) : null),
          (currentDrawer) => visibleFocusable(currentDrawer)[0] || null,
          (currentDrawer) => currentDrawer,
        ]);
      });
    });
  };

  const initializeDynamicModals = () => {
    const initializeWithin = (root) => {
      if (!(root instanceof Element)) return;
      if (root.matches('custom-modal')) initializeModal(root);
      root.querySelectorAll('custom-modal').forEach(initializeModal);
    };

    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach(initializeWithin);
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  const initializeMobileMenu = () => {
    const menu = document.querySelector('.js-mobile-menu');
    const button = document.querySelector('.js-mobile-menu-button');
    const openControl = document.querySelector('.js-mobile-menu-trigger');
    const closeControl = document.querySelector('.js-mobile-menu-close');
    if (!menu || !button || !openControl || !closeControl) return;

    menu.setAttribute('tabindex', '-1');

    const childMenus = Array.from(menu.querySelectorAll('.js-menu-item-child'));
    const actions = Array.from(menu.querySelectorAll('.js-menu-item-has-child, .js-menu-item-back'));
    const childStates = new WeakMap();
    const suppressedLevelTargets = new WeakMap();
    let menuWasOpen = false;
    const activate = (control) =>
      control.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

    actions.forEach((action) => {
      action.setAttribute('role', 'button');
      action.setAttribute('tabindex', '0');
      action.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        action.click();
      });
    });

    const childIsOpen = (child) => child.style.transform === 'translateX(0px)' || child.style.transform === 'translateX(0)';
    const levelTargetsFor = (child) => {
      const owner = child.parentElement;
      const level = owner?.parentElement;
      if (!owner || !level) return [];

      const targets = Array.from(level.children).filter((candidate) => candidate !== owner);
      const opener = owner.querySelector(':scope > .js-menu-item-has-child');
      if (opener) targets.push(opener);

      if (!level.matches('.js-menu-item-child')) {
        Array.from(level.parentElement?.children || []).forEach((candidate) => {
          if (candidate !== level) targets.push(candidate);
        });
      }

      return Array.from(new Set(targets));
    };
    const suppressLevel = (child) => {
      const targets = levelTargetsFor(child).map((target) => ({
        target,
        hadInert: target.hasAttribute('inert'),
        ariaHidden: target.getAttribute('aria-hidden'),
      }));
      suppressedLevelTargets.set(child, targets);
      targets.forEach(({ target }) => {
        target.setAttribute('inert', '');
        target.setAttribute('aria-hidden', 'true');
      });
    };
    const restoreLevel = (child) => {
      const targets = suppressedLevelTargets.get(child) || [];
      targets.forEach(({ target, hadInert, ariaHidden }) => {
        target.toggleAttribute('inert', hadInert);
        if (ariaHidden == null) target.removeAttribute('aria-hidden');
        else target.setAttribute('aria-hidden', ariaHidden);
      });
      suppressedLevelTargets.delete(child);
    };
    const syncChild = (child) => {
      const isOpen = childIsOpen(child);
      const hadFocus = child.contains(document.activeElement);
      if (childStates.get(child) === isOpen) return;
      childStates.set(child, isOpen);

      if (isOpen) {
        child.removeAttribute('inert');
        child.setAttribute('aria-hidden', 'false');
        (visibleFocusable(child)[0] || child).focus();
        suppressLevel(child);
      } else {
        restoreLevel(child);
        if (hadFocus) child.parentElement?.querySelector(':scope > .js-menu-item-has-child')?.focus();
        child.setAttribute('inert', '');
        child.setAttribute('aria-hidden', 'true');
      }
    };

    childMenus.forEach((child) => {
      child.setAttribute('tabindex', '-1');
      child.setAttribute('aria-hidden', 'true');
      child.setAttribute('inert', '');
      childStates.set(child, false);
      new MutationObserver(() => syncChild(child)).observe(child, {
        attributes: true,
        attributeFilter: ['style'],
      });
    });

    const syncMenu = () => {
      const isOpen = menu.style.height !== '' && menu.style.height !== '0px' && menu.style.height !== '0';
      const hadFocus = menu.contains(document.activeElement);
      menu.toggleAttribute('inert', !isOpen);
      menu.setAttribute('aria-hidden', String(!isOpen));
      button.setAttribute('aria-expanded', String(isOpen));
      if (isOpen === menuWasOpen) return;
      menuWasOpen = isOpen;

      if (isOpen) {
        window.setTimeout(() => (visibleFocusable(menu)[0] || menu).focus(), 50);
      } else {
        childMenus.forEach((child) => {
          child.style.transform = 'translateX(-100%)';
        });
        if (hadFocus) button.focus();
      }
    };

    button.addEventListener('click', (event) => {
      if (event.target.closest('.js-mobile-menu-trigger, .js-mobile-menu-close')) return;
      if (button.getAttribute('aria-expanded') === 'true') activate(closeControl);
      else activate(openControl);
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const openChildren = childMenus.filter(childIsOpen);
        const deepest = openChildren[openChildren.length - 1];
        const back = deepest?.querySelector('.js-menu-item-back');
        activate(back || closeControl);
        return;
      }
      trapFocus(event, menu);
    });

    new MutationObserver(syncMenu).observe(menu, { attributes: true, attributeFilter: ['style'] });
  };

  // Fancybox autofocuses the embedded video iframe. That iframe is cross-origin, so every
  // key the visitor presses - Escape included - is delivered to the video's document and
  // never reaches ours: a capture-phase keydown listener here records nothing at all. Move
  // focus to Fancybox's own close button, which lives in this document, and Escape closes
  // the lightbox and returns focus to the trigger. Image galleries already keep focus in
  // this document, so they are left alone.
  const initializeFancyboxEscape = () => {
    // This build of Fancybox names the lightbox close button .f-button.is-close-btn; the
    // other two are the names used by neighbouring versions, kept so a library bump does
    // not silently drop the fix.
    const closeSelector =
      '.f-button.is-close-btn,.f-button.is-close,[data-fancybox-close],.carousel__button.is-close';

    const focusClose = (container) => {
      const close = container.querySelector(closeSelector);
      if (!close) return false;
      const active = document.activeElement;
      // Only take focus back from the iframe or from nothing. If the visitor has already
      // moved focus to a real control in the lightbox, leave it where they put it.
      const stealable = !active || active === document.body || active.tagName === 'IFRAME';
      if (!stealable && container.contains(active)) return true;
      close.focus();
      return document.activeElement === close;
    };

    // Fancybox inserts the container first and loads its content after, so the iframe is
    // not there yet when the observer fires. Re-check for it on every tick rather than
    // once up front, which is what keeps image galleries - never any iframe - untouched.
    const claimFocus = (container) => {
      [0, 100, 300, 600].forEach((delay) => {
        window.setTimeout(() => {
          if (!container.isConnected) return;
          if (!container.querySelector('iframe')) return;
          focusClose(container);
        }, delay);
      });
    };

    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('.fancybox__container')) claimFocus(node);
          node.querySelectorAll('.fancybox__container').forEach(claimFocus);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('custom-modal').forEach(initializeModal);
    document.querySelectorAll('mega-menu').forEach(initializeMegaMenu);
    initializeCartAnnouncements();
    initializeCartFocusRestoration();
    initializeMobileMenu();
    initializeDynamicModals();
    initializeFancyboxEscape();
  });
})();
